<template>
  <div class="rpa-duration" @click.stop>
    <div v-if="runtime" :class="['rpa-duration__runtime', { 'rpa-duration__runtime--compact': compact }]" role="status" aria-live="polite">
      <template v-if="compact">
        <span>{{ view.short }}</span>
      </template>
      <template v-else>
        <div class="rpa-duration__heading"><span>{{ view.title }}</span></div>
        <strong class="rpa-duration__value">{{ view.value }}</strong>
        <div v-if="view.range" class="rpa-duration__range">{{ view.range }}</div>
        <div v-if="view.detail" class="rpa-duration__detail">{{ view.detail }}</div>
        <div class="rpa-duration__note">{{ view.note }}</div>
      </template>
    </div>
    <v-menu v-else v-model="open" offset-y left :close-on-content-click="false" max-width="360">
      <template v-slot:activator="{ on, attrs }">
        <button type="button" class="rpa-duration__trigger" v-bind="attrs" v-on="on" :aria-label="view.short" data-testid="rpa-duration-estimate">
          <v-icon size="17" color="#687EFF">mdi-clock-outline</v-icon>
          <span>{{ view.short }}</span>
        </button>
      </template>
      <section class="rpa-duration__menu">
        <div class="rpa-duration__heading">
          <strong>예상 소요 시간</strong>
          <div class="rpa-duration__actions">
            <button type="button" :disabled="state.loading || !target.ready" aria-label="예상 시간 다시 조회" @click="refresh"><v-icon size="18">mdi-refresh</v-icon></button>
            <button type="button" aria-label="예상 시간 닫기" @click="open = false"><v-icon size="18">mdi-close</v-icon></button>
          </div>
        </div>
        <div :class="['rpa-duration__card', { 'rpa-duration__card--warning': view.warning }]" role="status" aria-live="polite">
          <div class="rpa-duration__eyebrow">{{ view.reference ? '참고용 예측' : view.title }}</div>
          <strong class="rpa-duration__value">{{ view.value }}</strong>
          <div v-if="view.range" class="rpa-duration__range">{{ view.range }}</div>
        </div>
        <p class="rpa-duration__note">{{ view.note }}</p>
      </section>
    </v-menu>
  </div>
</template>

<script>
import http from '@/utils/http-common'
import { createDurationController, durationPresentation } from '@/features/rpa/duration.mjs'

export default {
  name: 'RpaDuration',
  props: {
    rpaId: { type: [Number, String], default: null },
    options: { type: Object, default: () => ({}) },
    revision: { type: [Number, String], default: '' },
    ready: { type: Boolean, default: true },
    runtime: Boolean,
    executionToken: { type: String, default: '' },
    status: { type: String, default: '' },
    compact: Boolean,
  },
  data: () => ({ open: false, state: { payload: null, loading: false, error: null, waiting: false }, durationController: null }),
  computed: {
    target() {
      const store = this.$store.state, user = store.user_info || {}
      return {
        rpaId: this.rpaId, options: this.options, revision: this.revision, runtime: this.runtime,
        executionToken: this.executionToken, status: this.status, ready: this.ready && Boolean(store.access_token),
        ownerKey: JSON.stringify([user.pk || user.id, user.tenant_id, user.organization_root, store.current_project_pk, this.$store.getters.permissionLevel]),
      }
    },
    view() { return durationPresentation(this.state, { runtime: this.runtime, status: this.status }) },
  },
  watch: { target(value) { this.durationController?.update(value) } },
  created() {
    this.durationController = createDurationController({
      http, onChange: state => { this.state = state },
      config: () => ({ headers: { Authorization: `Bearer ${this.$store.state.access_token}` } }),
    })
    this.durationController.update(this.target)
  },
  beforeDestroy() { this.durationController.stop() },
  methods: { refresh() { this.durationController.refresh() } },
}
</script>

<style scoped>
.rpa-duration { display: inline-flex; flex-shrink: 0; text-align: left; font-family: inherit; }
.rpa-duration__trigger { display: inline-flex; align-items: center; gap: 6px; height: 100%; min-height: 30px; box-sizing: border-box; padding: 0 9px; border: 1px solid #E8EAF2; border-radius: 8px; background: #fff; color: #414458; font-size: 12px; white-space: nowrap; cursor: pointer; }
.rpa-duration__trigger:hover { border-color: #B9C3FF; background: #F8F9FF; }
.rpa-duration__menu { width: 360px; max-width: calc(100vw - 24px); padding: 20px; background: #fff; color: #414458; border: 1px solid #E8EAF2; border-radius: 12px; text-align: left; }
.rpa-duration__heading { display: flex; justify-content: space-between; align-items: center; gap: 12px; font-size: 13px; }
.rpa-duration__actions { display: flex; gap: 4px; }
.rpa-duration__actions button { display: grid; place-items: center; width: 24px; height: 24px; border-radius: 4px; }
.rpa-duration__actions button:disabled { opacity: .4; cursor: default; }
.rpa-duration__card { margin-top: 16px; padding: 18px; border: 1px solid #E4E8FA; border-radius: 10px; background: #F8F9FF; }
.rpa-duration__card--warning { background: #FFFAF2; border-color: #F2E1C2; }
.rpa-duration__eyebrow { color: #676B82; font-size: 12px; margin-bottom: 10px; }
.rpa-duration__value { display: block; color: #414458; font-size: 19px; line-height: 1.5; font-weight: 600; letter-spacing: -.4px; }
.rpa-duration__range { color: #5065D9; font-size: 12px; margin-top: 5px; }
.rpa-duration__detail { color: #73788F; font-size: 12px; line-height: 1.6; margin-top: 10px; }
.rpa-duration__note { color: #73788F; font-size: 11px; line-height: 1.7; margin: 12px 0 0; }
.rpa-duration__runtime { width: 280px; max-width: 100%; margin-top: 14px; padding: 16px; border: 1px solid #E4E8FA; border-radius: 10px; background: #F8F9FF; }
.rpa-duration__runtime .rpa-duration__heading { color: #676B82; font-size: 12px; margin-bottom: 8px; }
.rpa-duration__runtime--compact { display: flex; align-items: center; gap: 6px; width: auto; margin-top: 2px; padding: 0; border: 0; background: transparent; color: #687EFF; font-size: 11px; line-height: 16px; }
.rpa-duration button:focus-visible { outline: 2px solid #687EFF; outline-offset: 3px; }
</style>
